const getExtensionFromType = (type?: string) => {
  if (!type) return "png";

  const extension = type.split("/")[1];

  if (!extension) return "png";
  if (extension === "jpeg") return "jpg";
  if (extension === "svg+xml") return "svg";

  return extension;
};

export enum ImageProcessingMode {
  Avif = "avif",
  Original = "original",
  WithoutBackground = "without_background",
}

export const getImageProcessingMode = (
  withoutBackground?: boolean,
  preserveOriginal?: boolean,
) => {
  if (preserveOriginal) return ImageProcessingMode.Original;
  if (withoutBackground) return ImageProcessingMode.WithoutBackground;

  return ImageProcessingMode.Avif;
};

export const isOriginalImageProcessing = (mode?: ImageProcessingMode) =>
  mode === ImageProcessingMode.Original;

export const appendPreserveOriginal = (
  formData: FormData,
  preserveOriginal?: boolean,
) => {
  if (preserveOriginal) {
    formData.append("preserve_original", "1");
  }
};

export const appendImageProcessingFields = (
  formData: FormData,
  mode?: ImageProcessingMode,
) => {
  formData.append(
    "without_background",
    mode === ImageProcessingMode.WithoutBackground ? "1" : "0",
  );

  appendPreserveOriginal(formData, mode === ImageProcessingMode.Original);
};

export const hasPendingImageUpload = (
  imageChanged?: boolean | Record<string, boolean>,
  newImage?: string | null | Record<string, string | null | undefined>,
) => {
  if (typeof imageChanged === "boolean") {
    return imageChanged && Boolean(newImage);
  }

  if (typeof newImage === "object" && newImage !== null && !Array.isArray(newImage)) {
    return Object.entries(imageChanged ?? {}).some(
      ([field, changed]) => changed && Boolean(newImage[field]),
    );
  }

  return Object.values(imageChanged ?? {}).some(Boolean);
};

interface ResolveUploadFileParams {
  imageUrl: string;
  processedFile?: File;
  originalFile?: File;
  preserveOriginal?: boolean;
  fallbackName: string;
  fallbackType?: string;
}

export const resolveUploadFile = async ({
  imageUrl,
  processedFile,
  originalFile,
  preserveOriginal,
  fallbackName,
  fallbackType = "image/png",
}: ResolveUploadFileParams) => {
  const selectedFile = preserveOriginal
    ? originalFile ?? processedFile
    : processedFile;

  if (selectedFile) return selectedFile;

  const response = await fetch(imageUrl);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch image: ${response.status} ${response.statusText}`,
    );
  }

  const blob = await response.blob();
  const type = blob.type || fallbackType;
  const extension = getExtensionFromType(type);

  return new File([blob], `${fallbackName}.${extension}`, { type });
};
